<template>
  <Section
    :id="id"
    :aria-label="
      t('search.paramSearchCriteria', { param: t('manifests.manifests') })
    "
    class="manifest-search-builder search-builder"
  >
    <div
      class="filters flex flex-gap-1 flex-wrap flex-grow-1 align-items-start"
    >
      <template v-for="filter in filters" :key="filter">
        <component
          :is="availableFiltersMap.get(filter)"
          :id="`criteria-${filter}`"
          v-model="criteria"
        />
      </template>
    </div>

    <FilterSelect
      v-model="filters"
      :possible-options="possibleFilterOptions"
      append-to="body"
    />
  </Section>
</template>

<script lang="ts" setup>
import type { Component, Ref } from 'vue';
import { computed, ref, toRefs, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { useModelValue } from '@rygen/rygen-ui/composables';
import { mapEnumToTranslatedDropdownValues } from '@rygen/rygen-ui/i18n';

import { ManifestSearchCriteria, ManifestSearchFilter } from '@/model';
import { useCurrentUserStore } from '@/stores/CurrentUser';

import ManifestClientFilter from '@/components/manifest/filter/ManifestClientFilter.vue';
import ManifestDateFilter from '@/components/manifest/filter/ManifestDateFilter.vue';
import ManifestLocationFilter from '@/components/manifest/filter/ManifestLocationFilter.vue';
import ManifestNumberFilter from '@/components/manifest/filter/ManifestNumberFilter.vue';
import ManifestScacFilter from '@/components/manifest/filter/ManifestScacFilter.vue';
import ManifestSourceFilter from '@/components/manifest/filter/ManifestSourceFilter.vue';
import ManifestStatusFilter from '@/components/manifest/filter/ManifestStatusFilter.vue';
import FilterSelect from '@rygen/rygen-ui/components/search/FilterSelect.vue';
import Section from '@rygen/rygen-ui/components/ui/Section.vue';

const MANIFEST_FILTERS: Record<ManifestSearchFilter, Component> = {
  [ManifestSearchFilter.MANIFEST_NUMBER]: ManifestNumberFilter,
  [ManifestSearchFilter.CLIENT]: ManifestClientFilter,
  [ManifestSearchFilter.SCAC]: ManifestScacFilter,
  [ManifestSearchFilter.LOCATION]: ManifestLocationFilter,
  [ManifestSearchFilter.STATUS]: ManifestStatusFilter,
  [ManifestSearchFilter.SOURCE]: ManifestSourceFilter,
  [ManifestSearchFilter.MANIFEST_DATE]: ManifestDateFilter,
};

const DEFAULT_VISIBLE_FILTERS = [
  ManifestSearchFilter.MANIFEST_NUMBER,
  ManifestSearchFilter.CLIENT,
  ManifestSearchFilter.SCAC,
  ManifestSearchFilter.STATUS,
  ManifestSearchFilter.MANIFEST_DATE,
];

const props = defineProps({
  id: {
    type: String,
    required: true,
  },
  modelValue: {
    type: ManifestSearchCriteria,
    default: () => new ManifestSearchCriteria(),
  },
});
const { id, modelValue } = toRefs(props);
const emit = defineEmits<{
  (event: 'update:model-value', modelValue: ManifestSearchCriteria): void;
}>();

const { selectedDomainId } = useCurrentUserStore();
const { t } = useI18n();
const { internalValue: criteria } = useModelValue(modelValue, {
  emit,
  factoryMethod: (opts: Partial<ManifestSearchCriteria>) =>
    new ManifestSearchCriteria(opts),
});

const manifestSearchFilters = mapEnumToTranslatedDropdownValues(
  ManifestSearchFilter,
  'ManifestSearchFilter',
);

const filters: Ref<Set<ManifestSearchFilter | string>> = ref(
  new Set(DEFAULT_VISIBLE_FILTERS),
);

const possibleFilterOptions = computed(() => manifestSearchFilters);

const availableFiltersMap = computed(
  () => new Map<string, Component>(Object.entries(MANIFEST_FILTERS)),
);

watch(
  criteria,
  (newVal: ManifestSearchCriteria) => {
    if (newVal.manifestNumber) {
      filters.value.add(ManifestSearchFilter.MANIFEST_NUMBER);
    }
    if (newVal.ownerNaturalIds.length) {
      filters.value.add(ManifestSearchFilter.CLIENT);
    }
    if (newVal.scac) {
      filters.value.add(ManifestSearchFilter.SCAC);
    }
    if (
      newVal.locationId ||
      newVal.locationName ||
      newVal.locationCode ||
      newVal.city ||
      newVal.state ||
      newVal.postalCode ||
      newVal.country
    ) {
      filters.value.add(ManifestSearchFilter.LOCATION);
    }
    if (newVal.status.length) {
      filters.value.add(ManifestSearchFilter.STATUS);
    }
    if (newVal.source.length) {
      filters.value.add(ManifestSearchFilter.SOURCE);
    }
    if (newVal.manifestDate?.start || newVal.manifestDate?.end) {
      filters.value.add(ManifestSearchFilter.MANIFEST_DATE);
    }
  },
  { deep: true, immediate: true },
);

watch(
  filters,
  (
    newVal: Set<ManifestSearchFilter | string>,
    oldVal: Set<ManifestSearchFilter | string>,
  ) => {
    const removedFilters = [...oldVal].filter((f) => !newVal.has(f));
    removedFilters.forEach((filter) => {
      if (filter === ManifestSearchFilter.MANIFEST_NUMBER) {
        criteria.value.manifestNumber = undefined;
      } else if (filter === ManifestSearchFilter.CLIENT) {
        criteria.value.ownerNaturalIds = [];
      } else if (filter === ManifestSearchFilter.SCAC) {
        criteria.value.scac = undefined;
      } else if (filter === ManifestSearchFilter.LOCATION) {
        criteria.value.locationId = undefined;
        criteria.value.locationName = undefined;
        criteria.value.locationCode = undefined;
        criteria.value.city = undefined;
        criteria.value.state = undefined;
        criteria.value.postalCode = undefined;
        criteria.value.country = undefined;
      } else if (filter === ManifestSearchFilter.STATUS) {
        criteria.value.status = [];
      } else if (filter === ManifestSearchFilter.SOURCE) {
        criteria.value.source = [];
      } else if (filter === ManifestSearchFilter.MANIFEST_DATE) {
        criteria.value.manifestDate = undefined;
      }
    });
  },
);

watch(selectedDomainId, () => {
  criteria.value.ownerNaturalIds = [];
  criteria.value.locationId = undefined;
  criteria.value.locationName = undefined;
  criteria.value.locationCode = undefined;
  criteria.value.city = undefined;
  criteria.value.state = undefined;
  criteria.value.postalCode = undefined;
  criteria.value.country = undefined;
});
</script>

<style>
.manifest-search-builder {
  .section-data {
    display: flex;
    gap: var(--sp-1);
    justify-content: space-between;

    .filters {
      max-width: 100%;

      .popup-inputgroup {
        .popup-inputgroup-card {
          width: max-content;
          top: calc((var(--sp-8) * -1) - 1px);
        }
      }
    }
  }
}
</style>
